import { useEffect, useRef, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import client from '../adminApi';
import { BlocksEditor } from './BlocksEditor';
import { FormatToolbar } from './FormatToolbar';
import { cn } from '@/lib/utils';

// Shared inline upload button — posts straight to the media library and hands
// back the uploaded media record so editors can attach images without leaving
// the form. The public /api/media/… URL is what gets stored in the field.
function UploadButton({ alt, onUploaded, id, label = 'Upload' }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const doUpload = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await client.post(`/admin/media?alt=${encodeURIComponent(alt || file.name)}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(res.data.media);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  return (
    <span className="inline-flex items-center gap-2">
      <input
        ref={inputRef}
        id={`${id}-upload`}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
        className="sr-only"
        onChange={(e) => doUpload(e.target.files?.[0])}
        data-testid={`${id}-upload-input`}
      />
      <label
        htmlFor={`${id}-upload`}
        className={cn(
          'inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-navy-900 px-2.5 py-1.5 text-xs font-bold text-navy-900 hover:bg-navy-50',
          busy && 'pointer-events-none opacity-60'
        )}
        data-testid={`${id}-upload-btn`}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {busy ? 'Uploading…' : label}
      </label>
      {error && <span role="alert" className="text-xs font-semibold text-red-700" data-testid={`${id}-upload-error`}>{error}</span>}
    </span>
  );
}

// Plain textarea, optionally with a formatting toolbar (field.toolbar) that
// inserts the safe inline syntax (**bold**, *italic*, "- " lists, links).
function TextareaField({ field, value, onChange, id }) {
  const ref = useRef(null);
  return (
    <div>
      {field.toolbar && <FormatToolbar targetRef={ref} id={id} />}
      <textarea ref={ref} id={id} data-testid={id} rows={field.toolbar ? 5 : 3} value={value} onChange={(e) => onChange(e.target.value)} className={cn(inputCls, 'min-h-[5rem]')} />
    </div>
  );
}

const inputCls =
  'mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-navy-900 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900';

export function getPath(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

export function setPath(obj, path, value) {
  const keys = path.split('.');
  const next = { ...obj };
  let cursor = next;
  for (let i = 0; i < keys.length - 1; i += 1) {
    cursor[keys[i]] = { ...(cursor[keys[i]] || {}) };
    cursor = cursor[keys[i]];
  }
  cursor[keys[keys.length - 1]] = value;
  return next;
}

function defaultFor(field) {
  if (field.default !== undefined) return field.default;
  if (field.type === 'checkbox') return false;
  if (field.type === 'list' || field.type === 'multiselect' || field.type === 'items') return [];
  return '';
}

function AuthorSelect({ value, onChange, id }) {
  const [authors, setAuthors] = useState([]);
  useEffect(() => {
    client
      .get('/admin/content/authors', { params: { per_page: 100 } })
      .then((res) => setAuthors(res.data.items))
      .catch(() => setAuthors([]));
  }, []);
  return (
    <select id={id} data-testid={id} value={value || ''} onChange={(e) => onChange(e.target.value || null)} className={inputCls}>
      <option value="">Amden Group (no named author)</option>
      {authors.map((a) => (
        <option key={a.authorId} value={a.authorId}>
          {`${a.name} — ${a.role}`}
        </option>
      ))}
    </select>
  );
}

function ListField({ value, onChange, id, help }) {
  const text = Array.isArray(value) ? value.join(', ') : '';
  return (
    <>
      <input
        id={id}
        value={text}
        onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
        className={inputCls}
        placeholder="Item one, item two"
      />
      {help && <p className="mt-1 text-xs text-slate-400">{help}</p>}
    </>
  );
}

// Ordered rows of {title, text} — used for product-page feature sections,
// audiences, stages and similar repeatable content.
function ItemsField({ value, onChange, id, field }) {
  const items = Array.isArray(value) ? value : [];
  const keys = field?.keys || ['title', 'text'];
  const placeholders = field?.placeholders || ['Title', 'Description'];
  const [k1, k2] = keys;
  const update = (i, patch) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  return (
    <div className="mt-1 space-y-2" data-testid={`${id}-items`}>
      {items.map((item, i) => (
        <div key={i} className="rounded-md border border-slate-200 bg-white p-2.5">
          <div className="flex items-center gap-2">
            <input
              aria-label={`Item ${i + 1} ${k1}`}
              value={item[k1] || ''}
              onChange={(e) => update(i, { [k1]: e.target.value })}
              placeholder={placeholders[0]}
              className="h-9 flex-1 rounded-md border border-slate-300 px-2.5 text-sm font-semibold"
            />
            <button
              type="button"
              aria-label={`Remove item ${i + 1}`}
              onClick={() => onChange(items.filter((_, j) => j !== i))}
              className="rounded border border-slate-300 px-2 py-1.5 text-xs font-bold text-red-700 hover:bg-red-50"
            >
              Remove
            </button>
          </div>
          <textarea
            aria-label={`Item ${i + 1} ${k2}`}
            rows={2}
            value={item[k2] || ''}
            onChange={(e) => update(i, { [k2]: e.target.value })}
            placeholder={placeholders[1]}
            className="mt-2 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
          />
        </div>
      ))}
      <button
        type="button"
        data-testid={`${id}-add-item`}
        onClick={() => onChange([...items, { [k1]: '', [k2]: '' }])}
        className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-bold text-navy-900 hover:bg-navy-50"
      >
        + Add item
      </button>
    </div>
  );
}

function ImageField({ value, onChange, id }) {
  const img = value || { src: '', alt: '' };
  return (
    <div className="mt-1 rounded-md border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs font-bold text-slate-500" htmlFor={`${id}-src`}>
          Image URL
        </label>
        <UploadButton
          id={id}
          alt={img.alt}
          onUploaded={(media) => onChange({ ...img, src: media.url, alt: img.alt || media.alt || '' })}
        />
      </div>
      <input
        id={`${id}-src`}
        value={img.src || ''}
        onChange={(e) => onChange({ ...img, src: e.target.value })}
        placeholder="https://… or upload in Media, then paste its /api/media/… URL"
        className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm"
      />
      <label className="mt-2 block text-xs font-bold text-slate-500" htmlFor={`${id}-alt`}>
        Alt text
      </label>
      <input
        id={`${id}-alt`}
        value={img.alt || ''}
        onChange={(e) => onChange({ ...img, alt: e.target.value })}
        className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm"
      />
      {img.src && (
        <img src={img.src} alt={img.alt || 'Preview'} className="mt-3 h-24 w-auto rounded-md border border-slate-200 object-cover" />
      )}
    </div>
  );
}

// Ordered rows of {src, alt, caption} — project galleries on case studies.
// Images are uploaded via the Media library first; the /api/media/… URL is
// pasted here (same convention as the single ImageField).
function GalleryField({ value, onChange, id }) {
  const items = Array.isArray(value) ? value : [];
  const update = (i, patch) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  return (
    <div className="mt-1 space-y-2" data-testid={`${id}-gallery`}>
      {items.map((item, i) => (
        <div key={i} className="rounded-md border border-slate-200 bg-white p-2.5">
          <div className="flex items-start gap-3">
            {item.src ? (
              <img src={item.src} alt={item.alt || ''} className="h-14 w-20 shrink-0 rounded border border-slate-200 object-cover" />
            ) : (
              <span className="flex h-14 w-20 shrink-0 items-center justify-center rounded border border-dashed border-slate-300 text-[10px] font-bold uppercase text-slate-400">No image</span>
            )}
            <div className="flex-1 space-y-1.5">
              <input
                aria-label={`Gallery image ${i + 1} URL`}
                value={item.src || ''}
                onChange={(e) => update(i, { src: e.target.value })}
                placeholder="/api/media/… URL (upload in Media first)"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              />
              <input
                aria-label={`Gallery image ${i + 1} alt text`}
                value={item.alt || ''}
                onChange={(e) => update(i, { alt: e.target.value })}
                placeholder="Alt text (required for accessibility)"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              />
              <input
                aria-label={`Gallery image ${i + 1} caption`}
                value={item.caption || ''}
                onChange={(e) => update(i, { caption: e.target.value })}
                placeholder="Caption (optional)"
                className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm"
              />
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <UploadButton
                id={`${id}-row-${i}`}
                alt={item.alt}
                label="Upload"
                onUploaded={(media) => update(i, { src: media.url, alt: item.alt || media.alt || '' })}
              />
              <button
                type="button"
                aria-label={`Remove gallery image ${i + 1}`}
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                className="rounded border border-slate-300 px-2 py-1.5 text-xs font-bold text-red-700 hover:bg-red-50"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid={`${id}-add-image`}
          onClick={() => onChange([...items, { src: '', alt: '', caption: '' }])}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-bold text-navy-900 hover:bg-navy-50"
        >
          + Add image
        </button>
        <UploadButton
          id={`${id}-new`}
          label="Upload new image"
          onUploaded={(media) => onChange([...items, { src: media.url, alt: media.alt || '', caption: '' }])}
        />
      </div>
    </div>
  );
}

function GroupChild(props) {
  return Field(props);
}

export function Field({ field, doc, onChange }) {
  if (field.showIf && !field.showIf(doc)) return null;
  const id = `f-${field.key.replace(/\./g, '-')}`;
  const raw = getPath(doc, field.key);
  const value = raw === undefined || raw === null ? defaultFor(field) : raw;
  const set = (v) => onChange(setPath(doc, field.key, v));

  if (field.type === 'group') {
    // field.path lets several fieldsets write into the same object
    // (distinct React keys, shared document path prefix).
    const prefix = field.path || field.key;
    return (
      <fieldset className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <legend className="px-1 text-sm font-extrabold text-navy-900">{field.label}</legend>
        <div className="mt-2 space-y-3">
          {field.children.map((child) => (
            <GroupChild key={child.key} field={{ ...child, key: `${prefix}.${child.key}` }} doc={doc} onChange={onChange} />
          ))}
        </div>
      </fieldset>
    );
  }

  const label = (
    <label htmlFor={id} className="text-sm font-bold text-navy-900">
      {field.label} {field.required && <span className="text-gold-700" aria-hidden="true">*</span>}
    </label>
  );

  return (
    <div>
      {field.type !== 'checkbox' && label}
      {field.type === 'textarea' && <TextareaField field={field} value={value} onChange={set} id={id} />}
      {field.type === 'text' && <input id={id} data-testid={id} type="text" value={value} onChange={(e) => set(e.target.value)} className={inputCls} />}
      {field.type === 'number' && (
        <input id={id} data-testid={id} type="number" value={value} onChange={(e) => set(e.target.value === '' ? null : Number(e.target.value))} className={inputCls} />
      )}
      {field.type === 'date' && <input id={id} data-testid={id} type="date" value={String(value).slice(0, 10)} onChange={(e) => set(e.target.value)} className={inputCls} />}
      {field.type === 'datetime' && (
        <input id={id} data-testid={id} type="datetime-local" value={String(value).slice(0, 16)} onChange={(e) => set(e.target.value ? new Date(e.target.value).toISOString() : null)} className={inputCls} />
      )}
      {field.type === 'select' && (
        <select id={id} data-testid={id} value={value} onChange={(e) => set(e.target.value)} className={inputCls}>
          {!field.required && <option value="">—</option>}
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
      {field.type === 'multiselect' && (
        <div className="mt-1.5 flex flex-wrap gap-2" role="group" aria-label={field.label}>
          {field.options.map((o) => {
            const active = (value || []).includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                aria-pressed={active}
                onClick={() => set(active ? value.filter((v) => v !== o.value) : [...(value || []), o.value])}
                className={cn(
                  'rounded-full border-2 px-3 py-1.5 text-xs font-bold transition-colors',
                  active ? 'border-navy-900 bg-navy-900 text-white' : 'border-slate-200 bg-white text-navy-800 hover:border-navy-300'
                )}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}
      {field.type === 'checkbox' && (
        <label htmlFor={id} className="mt-1 flex items-start gap-2.5">
          <input id={id} data-testid={id} type="checkbox" checked={!!value} onChange={(e) => set(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300" />
          <span className="text-sm font-semibold text-navy-900">{field.label}</span>
        </label>
      )}
      {field.type === 'slug' && (
        <div className="mt-1 flex items-center gap-2">
          <input id={id} data-testid={id} type="text" value={value} onChange={(e) => set(e.target.value)} className={inputCls} />
          <button
            type="button"
            onClick={() => {
              const src = getPath(doc, field.source || 'title') || '';
              set(src.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80));
            }}
            className="shrink-0 rounded-md border border-slate-300 px-3 py-2 text-xs font-bold text-navy-900 hover:bg-navy-50"
          >
            Generate
          </button>
        </div>
      )}
      {field.type === 'list' && <ListField value={value} onChange={set} id={id} help={field.help} />}
      {field.type === 'items' && <ItemsField value={value} onChange={set} id={id} field={field} />}
      {field.type === 'image' && <ImageField value={value} onChange={set} id={id} />}
      {field.type === 'gallery' && <GalleryField value={value} onChange={set} id={id} />}
      {field.type === 'blocks' && <BlocksEditor value={value} onChange={set} id={id} />}
      {field.type === 'author' && <AuthorSelect value={value} onChange={set} id={id} />}
      {field.help && field.type !== 'list' && <p className="mt-1 text-xs text-slate-400">{field.help}</p>}
    </div>
  );
}
